import { UzBadge } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';

import { DELIVERY_OUTCOME_LABEL, DELIVERY_OUTCOME_TONE, formatDateTime } from '../labels';
import { useLpsConnections, useLpsDeliveries } from '../use-lps';

import { LpsDeliveryDrawer } from './LpsDeliveryDrawer';

import type { LpsDeliveryItem, LpsDeliveryListQuery, LpsDeliveryOutcome } from '../lps.api';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';

/**
 * Every request that reached an LPS webhook - the ones that became leads and
 * the ones that did not (wrong secret, bad signature, no phone or email,
 * provider retries, crashes). Kept 90 days. Answers "the provider says it sent
 * it - did we get it?".
 */
export const LpsDeliveriesTab = (): React.JSX.Element => {
  const [openId, setOpenId] = useState<string | null>(null);
  const connections = useLpsConnections();

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'outcome',
        label: 'Outcome',
        options: [
          { value: '', label: 'All deliveries' },
          { value: 'PROBLEMS', label: 'Problems only' },
          ...Object.entries(DELIVERY_OUTCOME_LABEL).map(([value, label]) => ({ value, label })),
        ],
      },
      {
        id: 'connectionId',
        label: 'Connection',
        widthClass: 'w-56',
        options: [
          { value: '', label: 'All connections' },
          ...(connections.data ?? []).map((c) => ({ value: c.id, label: c.name })),
        ],
      },
    ],
    [connections.data],
  );

  const columns: ListScreenColumns<LpsDeliveryItem> = useMemo(
    () => () => {
      const cols: ReadonlyArray<DataGridColumn<LpsDeliveryItem>> = [
        {
          id: 'receivedAt',
          header: 'Received',
          sortable: true,
          cell: (row) => (
            <span className="text-xs whitespace-nowrap text-zinc-700">
              {formatDateTime(row.receivedAt)}
            </span>
          ),
        },
        {
          id: 'connection',
          header: 'Connection',
          cell: (row) => (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-zinc-900">
                {row.connectionName ?? 'Unknown connection'}
              </span>
              <span className="text-[11px] text-zinc-500">
                {row.connectorId ?? row.connectionId}
              </span>
            </div>
          ),
        },
        {
          id: 'outcome',
          header: 'Outcome',
          cell: (row) => (
            <UzBadge tone={DELIVERY_OUTCOME_TONE[row.outcome as LpsDeliveryOutcome]}>
              {DELIVERY_OUTCOME_LABEL[row.outcome as LpsDeliveryOutcome] ?? row.outcome}
            </UzBadge>
          ),
        },
        {
          id: 'counts',
          header: 'Leads',
          cell: (row) => (
            <span className="text-xs text-zinc-700 tabular-nums">
              {row.accepted} new · {row.duplicates} dup · {row.skipped} skipped
            </span>
          ),
        },
        {
          id: 'why',
          header: 'Why',
          cell: (row) => {
            const text = row.error ?? row.notes[0] ?? '';
            return text ? (
              <span
                className="block max-w-[340px] truncate text-xs text-zinc-600"
                title={row.notes.join('\n')}
              >
                {text}
              </span>
            ) : (
              <span className="text-xs text-zinc-400">-</span>
            );
          },
        },
        {
          id: 'request',
          header: 'Request',
          cell: (row) => (
            <span className="font-mono text-[11px] whitespace-nowrap text-zinc-500">
              {row.method} {row.httpStatus} · {(row.bodyBytes / 1024).toFixed(1)} KB
            </span>
          ),
        },
      ];
      return cols;
    },
    [],
  );

  return (
    <>
      <ListScreen<LpsDeliveryItem, LpsDeliveryListQuery>
        embedded
        gridId="lps-deliveries"
        title="Deliveries"
        tableLayout="auto"
        useList={useLpsDeliveries}
        defaultSort={{ by: 'receivedAt', dir: 'desc' }}
        toQuery={(state, base) => {
          const f = state.filters;
          return {
            page: base.page,
            pageSize: base.pageSize,
            sortDir: base.sortDir,
            ...(f.outcome ? { outcome: f.outcome as LpsDeliveryListQuery['outcome'] } : {}),
            ...(f.connectionId ? { connectionId: f.connectionId } : {}),
          };
        }}
        columns={columns}
        filters={filters}
        getRowId={(row) => row.id}
        onRowClick={(row) => setOpenId(row.id)}
        copy={{
          searchPlaceholder: 'Filter with the outcome and connection filters',
          emptyTitle: 'No deliveries yet',
          emptyDescription: 'Every request to a connection webhook shows here, kept for 90 days.',
          errorTitle: 'Failed to load deliveries',
          errorDescription: 'The server returned an error. Try refreshing.',
        }}
      />
      <LpsDeliveryDrawer deliveryId={openId} onClose={() => setOpenId(null)} />
    </>
  );
};
